import { ToastType, useToasts } from '@pbk-web/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

const SSO_SIGN_ERROR_PARAM = 'ssoSignError';

const notices = {
  'sign-expired': {
    type: ToastType.INFO,
    title: 'form-header.toasts.sso-sign-expired.title',
    description: 'form-header.toasts.sso-sign-expired.text',
  },
  'sign-failed': {
    type: ToastType.ERROR,
    title: 'form-header.toasts.sso-sign-failed.title',
    description: 'form-header.toasts.sso-sign-failed.text',
  },
} as const;

const isSsoSignError = (value: string | null): value is keyof typeof notices => value !== null && Object.hasOwn(notices, value);

/** Shows the notice for an `?ssoSignError=` SSO signing redirect once, then strips the param. */
export const useSsoSignErrorNotice = (): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToasts();
  const { t } = useTranslation('common');
  // Read on first render; the root redirect to /clients drops the query.
  const [signError] = useState(() => searchParams.get(SSO_SIGN_ERROR_PARAM));
  const shown = useRef(false);

  useEffect(() => {
    if (shown.current || !isSsoSignError(signError)) return;
    shown.current = true;

    const { type, title, description } = notices[signError];
    addToast({ type, title: t(title), description: t(description) });
    setSearchParams(
      (prev) => {
        prev.delete(SSO_SIGN_ERROR_PARAM);
        return prev;
      },
      { replace: true },
    );
  }, [signError, addToast, t, setSearchParams]);
};
